import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { telemetryService } from '@services/api/telemetryService';
import type { AppEvent } from '@/types/customer';
import type { EventSeverity } from '@/types/telemetry';
import '../CustomerTabs.css';
import './EventsTab.css';

interface EventsTabProps {
  customerId: string;
}

interface EventFilters {
  event_type: string;
  severity: EventSeverity | '';
  date_from: string;
  date_to: string;
}

interface EventDetailsModalProps {
  event: AppEvent;
  customerId: string;
  onClose: () => void;
}

const EventDetailsModal = ({ event, customerId, onClose }: EventDetailsModalProps) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Event Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="event-detail-grid">
            <div className="event-detail-item">
              <span className="event-detail-label">Event ID:</span>
              <span className="event-detail-value">{event.id}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Event Type:</span>
              <span className="event-detail-value">{event.event_type}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Severity:</span>
              <span className={`severity-badge severity-${event.severity}`}>
                {event.severity}
              </span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Screen:</span>
              <span className="event-detail-value">{event.screen || 'N/A'}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Action:</span>
              <span className="event-detail-value">{event.action || 'N/A'}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Created At:</span>
              <span className="event-detail-value">
                {new Date(event.created_at).toLocaleString()}
              </span>
            </div>
          </div>

          {event.error_message && (
            <div className="event-detail-section">
              <h3>Error Message</h3>
              <pre className="event-detail-code">{event.error_message}</pre>
            </div>
          )}

          {event.error_stack && (
            <div className="event-detail-section">
              <h3>Error Stack</h3>
              <pre className="event-detail-code">{event.error_stack}</pre>
            </div>
          )}

          {event.properties && Object.keys(event.properties).length > 0 && (
            <div className="event-detail-section">
              <h3>Properties</h3>
              <pre className="event-detail-code">
                {JSON.stringify(event.properties, null, 2)}
              </pre>
            </div>
          )}

          {event.device && Object.keys(event.device).length > 0 && (
            <div className="event-detail-section">
              <h3>Device Info</h3>
              <pre className="event-detail-code">
                {JSON.stringify(event.device, null, 2)}
              </pre>
            </div>
          )}

          {event.network && Object.keys(event.network).length > 0 && (
            <div className="event-detail-section">
              <h3>Network Info</h3>
              <pre className="event-detail-code">
                {JSON.stringify(event.network, null, 2)}
              </pre>
            </div>
          )}

          {event.session_id && (
            <div className="event-detail-section">
              <h3>Session</h3>
              <Link 
                to={`/customers/${customerId}/sessions/${event.session_id}`}
                className="btn btn-primary"
                onClick={onClose}
              >
                View Session Details →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EventsTab = ({ customerId }: EventsTabProps) => {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);
  const [summary, setSummary] = useState<{
    total: number;
    errors: number;
    warnings: number;
  } | null>(null);

  const [filters, setFilters] = useState<EventFilters>({
    event_type: '',
    severity: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    loadEvents();
  }, [customerId, filters]);

  useEffect(() => {
    loadSummary();
  }, [customerId]);

  const loadEvents = async () => {
    setLoading(true);
    const result = await telemetryService.getCustomerEvents(customerId, {
      event_type: filters.event_type || undefined,
      severity: filters.severity || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
    });
    
    if (result.success && result.data) {
      setEvents(result.data as AppEvent[]);
    }
    setLoading(false);
  };

  const loadSummary = async () => {
    const result = await telemetryService.getCustomerEventsSummary(customerId);
    if (result.success && result.data) {
      setSummary(result.data);
    }
  };

  const handleFilterChange = (key: keyof EventFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      event_type: '',
      severity: '',
      date_from: '',
      date_to: '',
    });
  };

  // Get unique event types from current events for filter dropdown
  const uniqueEventTypes = Array.from(new Set(events.map((e) => e.event_type))).sort();

  if (loading && events.length === 0) {
    return <div>Loading events...</div>;
  }

  return (
    <div className="tab-content-wrapper">
      {/* Summary Cards */}
      {summary && (
        <div className="events-summary">
          <div className="summary-card summary-total">
            <div className="summary-icon">📊</div>
            <div className="summary-content">
              <span className="summary-value">{summary.total}</span>
              <span className="summary-label">Total Issues</span>
            </div>
          </div>
          <div className="summary-card summary-errors">
            <div className="summary-icon">🔴</div>
            <div className="summary-content">
              <span className="summary-value">{summary.errors}</span>
              <span className="summary-label">Errors</span>
            </div>
          </div>
          <div className="summary-card summary-warnings">
            <div className="summary-icon">🟡</div>
            <div className="summary-content">
              <span className="summary-value">{summary.warnings}</span>
              <span className="summary-label">Warnings</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="info-card">
        <h3 className="info-card-title">🔍 Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Event Type:</label>
            <select
              className="filter-select"
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
            >
              <option value="">All Types</option>
              {uniqueEventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Severity:</label>
            <select
              className="filter-select"
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="warn">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Date From:</label>
            <input
              type="datetime-local"
              className="filter-input"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Date To:</label>
            <input
              type="datetime-local"
              className="filter-input"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>

          <button className="btn btn-secondary btn-small" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Events Table */}
      <div className="info-card">
        <h3 className="info-card-title">⚠️ Errors & Warnings ({events.length})</h3>
        
        {events.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">✅</span>
            <p className="text-secondary">No errors or warnings found for this customer</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Created At</th>
                  <th>Event Type</th>
                  <th>Severity</th>
                  <th>Screen</th>
                  <th>Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className={`event-row event-row-${event.severity}`}>
                    <td className="text-small">
                      {new Date(event.created_at).toLocaleString()}
                    </td>
                    <td className="text-small">{event.event_type}</td>
                    <td>
                      <span className={`severity-badge severity-${event.severity}`}>
                        {event.severity}
                      </span>
                    </td>
                    <td className="text-small">{event.screen || 'N/A'}</td>
                    <td className="text-small event-message">
                      {event.error_message 
                        ? (event.error_message.length > 60 
                            ? `${event.error_message.substring(0, 60)}...` 
                            : event.error_message)
                        : ((event.properties as Record<string, unknown>)?.error_message as string) || 'N/A'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-ghost btn-small"
                          onClick={() => setSelectedEvent(event)}
                        >
                          View Details
                        </button>
                        {event.session_id && (
                          <Link 
                            to={`/customers/${customerId}/sessions/${event.session_id}`}
                            className="btn btn-ghost btn-small"
                          >
                            View Session
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          customerId={customerId}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
};

